import '@/utils/disable-console';
import { HomeOutlined } from '@ant-design/icons';
import type { Settings as ProLayoutSettings } from '@ant-design/pro-components';
import { SettingDrawer } from '@ant-design/pro-components';
import type { RequestConfig, RunTimeLayoutConfig } from '@umijs/max';
import {
  history,
  setLocale,
  useAntdConfigSetter,
  useIntl,
  useLocation,
} from '@umijs/max';
import {
  type LayoutSettings as CoreLayoutSettings,
  type CoreSessionConfig,
  type CurrentUser,
  SessionProvider,
  useSession,
  useShell,
  useTenant,
  useTheme,
} from '@zhiyue/core';
import '@zhiyue/core/style.css';

import { sessionAdapter } from '@/adapters/sessionAdapter';
import { AvatarDropdown, Footer } from '@/components';
import SiderResizeHandle, {
  DEFAULT_SIDER_WIDTH,
  getStoredSiderWidth,
} from '@/components/Layout/SiderResizeHandle';
import {
  CollectionMenuItem,
  CollectionSectionHeader,
  CollectionTree,
} from '@/components/Layout/TalentCollectionMenu';
import {
  type ClientCollection,
  clientCollectionApi,
} from '@/services/api/client-collections';
import { clientApi } from '@/services/api/clients';
import { consultantPoolManagementApi } from '@/services/api/consultant-pool-management';
import { dataDictionaryApi } from '@/services/api/data-dictionaries';
import {
  type LightHunterAccess,
  lightHunterApi,
} from '@/services/api/light-hunter';
import { type MemberWithProfile, memberApi } from '@/services/api/members';
import {
  type ProjectCollection,
  projectCollectionApi,
} from '@/services/api/project-collections';
import { projectApi } from '@/services/api/projects';
import {
  type TalentCollection,
  talentCollectionApi,
} from '@/services/api/talent-collections';
import { talentApi } from '@/services/api/talents';
import { ensureSysUser } from '@/services/bff';
import {
  clearAutoRefresh,
  ensureValidAccessToken,
  hasOAuth2Session,
  redirectToSSO,
  setupAutoRefresh,
} from '@/services/oauth2';
import {
  getLayoutVisibility,
  updateSessionSnapshot,
} from '@/state/sessionSnapshot';
import {
  buildCollectionTree,
  getCollectionAggregateCount,
  getCollectionDescendantUuids,
} from '@/utils/collection-tree';
import { identifyUser } from '@/utils/tracking';
import '@ant-design/v5-patch-for-react-19';
import {
  theme as antdTheme,
  Form,
  Grid,
  Input,
  Modal,
  message,
  TreeSelect,
} from 'antd';
import { BizAppConfig } from 'config/application.manifest';
import {
  getBrandAppName,
  getStoredBrandLocale,
  syncBrandDocument,
} from 'config/brand';
import React from 'react';
import defaultSettings from '../config/defaultSettings';

const LightHunterChatUnreadObserver = React.lazy(
  () => import('@/components/light-hunter-chat-unread-observer'),
);

import { errorConfig } from './requestErrorConfig';

export const request: RequestConfig = {
  baseURL: BizAppConfig.appApiUrl,
  ...errorConfig,
};

const isDev = process.env.NODE_ENV === 'development';
const loginPath = '/user/login';
const TALENT_COLLECTION_ACTION_PATH = '/talents/__collection_action__';
const CLIENT_COLLECTION_ACTION_PATH = '/clients/__collection_action__';
const PROJECT_COLLECTION_ACTION_PATH = '/projects/__collection_action__';
const TALENT_COLLECTION_ITEM_PREFIX = '/talents/collection/';
const COLLECTION_GROUP_PATHS = new Set([
  '/talents/__collection_group__',
  '/clients/__collection_group__',
  '/projects/__collection_group__',
]);
const CLIENT_COLLECTION_ITEM_PREFIX = '/clients/collection/';
const PROJECT_COLLECTION_ITEM_PREFIX = '/projects/collection/';
const DATA_DICTIONARIES_PATH = '/data-dictionaries';
const TALENT_COLLECTIONS_UPDATED_EVENT = 'talent-collections-updated';
const LEGACY_COLLECTIONS_UPDATED_EVENT = 'collections-updated';
const DICTIONARY_PRELOAD_CODES = [
  'industry',
  'company_type',
  'client_source',
  'development_stage',
  'enterprise_nature',
  'company_scale',
] as const;
const COUNTABLE_MENU_PATHS = [
  '/talents/all',
  '/talents/my',
  '/talents/private',
  '/talents/public',
  '/clients/all',
  '/clients/my',
  '/clients/private',
  '/clients/public',
  '/projects/all',
  '/projects/my',
  '/projects/recommended',
  '/projects/responsible',
] as const;
const publicPaths = [
  loginPath,
  '/user/register',
  '/user/register-result',
  '/user/forgot-password',
  '/user/change-password',
  '/callback',
];

const getSettingsWithSiderWidth = (locale?: string) => ({
  ...defaultSettings,
  title: getBrandAppName(locale),
  siderWidth: getStoredSiderWidth(),
});

type CollectionKind = 'talent' | 'client' | 'project';

interface SidebarCollection {
  uuid: string;
  name: string;
  description?: string;
  parentUuid?: string | null;
  children?: SidebarCollection[];
  talentCount?: number;
  clientCount?: number;
  projectCount?: number;
}

interface CollectionFormValues {
  name: string;
  description?: string;
  parentUuid?: string | null;
}

interface CollectionConfig {
  title: string;
  prefix: string;
  itemPrefix: string;
  actionPath: string;
  collections: SidebarCollection[];
  countField: 'talentCount' | 'clientCount' | 'projectCount';
  countUnit: string;
  entityName: string;
  recordName: string;
  updatedEvent: string;
  api: {
    createCollection: (data: CollectionFormValues) => Promise<unknown>;
    updateCollection: (
      uuid: string,
      data: CollectionFormValues,
    ) => Promise<unknown>;
    deleteCollection: (uuid: string) => Promise<unknown>;
  };
}

interface CollectionTreeSelectOption {
  title: string;
  value: string;
  key: string;
  children?: CollectionTreeSelectOption[];
}

const buildCollectionTreeSelectData = (
  collections: SidebarCollection[],
  excludedUuid?: string,
): CollectionTreeSelectOption[] => {
  const excluded = getCollectionDescendantUuids(collections, excludedUuid);
  if (excludedUuid) excluded.add(excludedUuid);
  const visit = (nodes: SidebarCollection[]): CollectionTreeSelectOption[] =>
    nodes
      .filter((node) => !excluded.has(node.uuid))
      .map((node) => ({
        title: node.name,
        value: node.uuid,
        key: node.uuid,
        children: node.children?.length ? visit(node.children) : undefined,
      }));

  return visit(buildCollectionTree(collections));
};

const showCollectionError = (error: unknown, fallback: string) => {
  const requestError = error as {
    handled?: boolean;
    message?: string;
    response?: {
      data?: { message?: string | string[]; errorMessage?: string };
    };
  };
  if (requestError?.handled) return;
  const responseMessage =
    requestError?.response?.data?.message ||
    requestError?.response?.data?.errorMessage;
  message.error(
    (Array.isArray(responseMessage)
      ? responseMessage.join('；')
      : responseMessage) ||
      requestError?.message ||
      fallback,
  );
};

const SESSION_STORAGE_KEYS = {
  accessToken: 'access_token',
  refreshToken: 'refresh_token',
  user: 'user',
} as const;

const hasCachedOAuth2Session = (): boolean => {
  if (typeof window === 'undefined') {
    return false;
  }

  return Boolean(
    localStorage.getItem(SESSION_STORAGE_KEYS.accessToken) ||
      localStorage.getItem(SESSION_STORAGE_KEYS.refreshToken),
  );
};

const getCachedCurrentUser = ():
  | (API.CurrentUser & CurrentUser)
  | undefined => {
  if (typeof window === 'undefined') {
    return undefined;
  }

  const rawUser = localStorage.getItem(SESSION_STORAGE_KEYS.user);
  if (!rawUser) {
    return undefined;
  }

  try {
    return JSON.parse(rawUser) as API.CurrentUser & CurrentUser;
  } catch {
    localStorage.removeItem(SESSION_STORAGE_KEYS.user);
    return undefined;
  }
};

const getUserSyncKey = (
  user?:
    | (Partial<CurrentUser> & {
        uuid?: string;
      })
    | null,
) => {
  if (!user) {
    return '';
  }

  return [user.id, user.uuid, user.username, user.tenantUuid]
    .filter(Boolean)
    .join('|');
};

const getSessionConfig = (): CoreSessionConfig => {
  return {
    redirectAfterLogout: '/user/login',
    adapter: sessionAdapter,
    ui: {
      links: {
        help: 'https://docs.shanhuyun.cn/',
      },
      userMenu: {
        items: [],
      },
      locales: [
        { key: 'en-US', label: 'English', icon: '🇺🇸', title: 'Language' },
        { key: 'ja-JP', label: '日本語', icon: '🇯🇵', title: '言語' },
        { key: 'zh-CN', label: '简体中文', icon: '🇨🇳', title: '语言' },
        { key: 'zh-TW', label: '繁體中文', icon: '🇭🇰', title: '語言' },
      ],
    },
    featureFlags: {
      showHeader: true,
      showNav: true,
      showFooter: true,
      enableTenantSwitch: true,
      enableRegister: false,
    },
    onTenantSwitched: () => {
      window.location.href = '/';
    },
    onLocaleChanged: (locale) => {
      setLocale(locale, false);
    },
    onUnauthorized: () => {
      if (!publicPaths.includes(history.location.pathname)) {
        redirectToSSO();
      }
    },
  };
};

const appendCollectionActionMenus = (
  menus: any[],
  talentCollections: any[] = [],
  clientCollections: any[] = [],
  projectCollections: any[] = [],
  groupCollectionRoots = false,
): any[] =>
  menus.map((menu) => {
    const current = {
      ...menu,
      children: Array.isArray(menu.children)
        ? appendCollectionActionMenus(
            menu.children,
            talentCollections,
            clientCollections,
            projectCollections,
            groupCollectionRoots,
          )
        : menu.children,
    };

    const injectCollections = (
      targetChildren: any[],
      actionPath: string,
      actionName: string,
      list: any[],
      prefix: string,
      virtualPrefix: string,
    ) => {
      const nextChildren = [...targetChildren];
      if (!nextChildren.some((child) => child?.path === actionPath)) {
        nextChildren.push({ path: actionPath, name: actionName });
      }
      const collectionItems = list.length
        ? [
            {
              path: `${virtualPrefix}__tree__`,
              name: '收藏夹树',
              locale: false,
            },
          ]
        : [];
      if (groupCollectionRoots) {
        const groupPath = `${prefix}/__collection_group__`;
        if (
          collectionItems.length > 0 &&
          !nextChildren.some((child) => child?.path === groupPath)
        ) {
          nextChildren.push({
            path: groupPath,
            name: '收藏夹列表',
            locale: false,
            children: collectionItems,
          });
        }
        return nextChildren;
      }
      collectionItems.forEach((collectionItem) => {
        if (
          !nextChildren.some((child) => child?.path === collectionItem.path)
        ) {
          nextChildren.push(collectionItem);
        }
      });
      return nextChildren;
    };

    const children = Array.isArray(current.children)
      ? [...current.children]
      : [];

    if (current.path === '/talents') {
      return {
        ...current,
        children: injectCollections(
          children,
          TALENT_COLLECTION_ACTION_PATH,
          '__talentCollectionAction',
          talentCollections,
          '/talents',
          TALENT_COLLECTION_ITEM_PREFIX,
        ),
      };
    }

    if (current.path === '/clients') {
      return {
        ...current,
        children: injectCollections(
          children,
          CLIENT_COLLECTION_ACTION_PATH,
          '__clientCollectionAction',
          clientCollections,
          '/clients',
          CLIENT_COLLECTION_ITEM_PREFIX,
        ),
      };
    }

    if (current.path === '/projects') {
      return {
        ...current,
        children: injectCollections(
          children,
          PROJECT_COLLECTION_ACTION_PATH,
          '__projectCollectionAction',
          projectCollections,
          '/projects',
          PROJECT_COLLECTION_ITEM_PREFIX,
        ),
      };
    }

    return current;
  });

const filterLightHunterMenus = (
  menus: any[],
  access?: LightHunterAccess | null,
): any[] => {
  const isManagement = access?.isManagement === true;
  const hasTaskAccess = !access || access.permissions.viewTaskCenter;
  const allowedForMember = new Set([
    '/light-hunter',
    '/light-hunter/dashboard',
    '/light-hunter/tasks',
  ]);
  return menus
    .map((menu) => {
      if (menu.path !== '/light-hunter') return menu;
      const children = Array.isArray(menu.children)
        ? filterLightHunterMenus(menu.children, access)
        : menu.children;
      return { ...menu, children };
    })
    .filter((menu) => {
      if (menu.path === '/light-hunter' && !isManagement) return hasTaskAccess;
      if (
        isManagement ||
        !String(menu.path || '').startsWith('/light-hunter')
      ) {
        return true;
      }
      if (!hasTaskAccess) return false;
      return allowedForMember.has(menu.path);
    });
};

const filterConsultantManagementMenus = (
  menus: any[],
  canManageConsultants: boolean,
) =>
  menus.filter(
    (menu) => canManageConsultants || menu.path !== '/team-management',
  );
const HeaderTitle: React.FC<{
  logo: React.ReactNode;
  appTitle: string;
  workbenchUrl?: string;
}> = ({ logo, appTitle, workbenchUrl }) => {
  const intl = useIntl();
  const workbenchLabel = intl.formatMessage({ id: 'menu.account.workbench' });

  return (
    <div className="app-header-title-root">
      {/* <button
        type="button"
        className="app-header-title-main"
        onClick={() => history.push('/')}
      >
        {logo}
        <span className="app-header-title-name">{appTitle}</span>
      </button> */}
      <button
        type="button"
        className="app-header-workbench-btn"
        onClick={() => {
          if (!workbenchUrl) {
            return;
          }
          window.open(workbenchUrl, '_self');
        }}
        disabled={!workbenchUrl}
        title={workbenchLabel}
      >
        <HomeOutlined />
        <span className="app-header-workbench-text">{workbenchLabel}</span>
      </button>
    </div>
  );
};

const SessionSync: React.FC<{ setInitialState: (updater: any) => void }> = ({
  setInitialState,
}) => {
  const { navTheme } = useTheme();
  const { currentUser, loading } = useSession();
  const { shellConfig } = useShell();
  const setAntdConfig = useAntdConfigSetter();
  const [runtimeLocale, setRuntimeLocale] = React.useState(
    getStoredBrandLocale(),
  );
  const themeSyncedRef = React.useRef<CoreLayoutSettings['navTheme'] | null>(
    null,
  );
  const syncedRef = React.useRef({
    navTheme: '',
    userSyncKey: '',
    loading: true,
  });
  const sysUserEnsuredForRef = React.useRef('');
  const identifiedUserKeyRef = React.useRef('');
  const userSyncKey = React.useMemo(
    () => getUserSyncKey(currentUser),
    [currentUser],
  );

  React.useEffect(() => {
    if (
      !currentUser ||
      !userSyncKey ||
      identifiedUserKeyRef.current === userSyncKey
    ) {
      return;
    }
    identifiedUserKeyRef.current = userSyncKey;

    const user = currentUser as Partial<CurrentUser> & {
      uuid?: string;
      id?: string;
    };
    const uniqueId = String(user.uuid || user.id || user.username || '');
    identifyUser(uniqueId, {
      username: user.username,
      name: user.name,
      tenant: user.tenantUuid,
    });
  }, [currentUser, userSyncKey]);

  React.useEffect(() => {
    updateSessionSnapshot({
      currentUser,
      loading,
      featureFlags: shellConfig.featureFlags,
    });

    if (typeof setInitialState !== 'function') {
      return;
    }

    const lastSynced = syncedRef.current;
    if (
      lastSynced.navTheme === navTheme &&
      lastSynced.userSyncKey === userSyncKey &&
      lastSynced.loading === loading
    ) {
      return;
    }
    setInitialState((prev: any) => {
      if (!prev) return prev;

      const nextCurrentUser =
        loading && typeof currentUser === 'undefined'
          ? prev.currentUser
          : currentUser;
      const nextUserSyncKey = getUserSyncKey(nextCurrentUser);
      const prevUserSyncKey = getUserSyncKey(prev.currentUser);
      if (
        prev.settings?.navTheme === navTheme &&
        prevUserSyncKey === nextUserSyncKey &&
        prev.loading === loading
      ) {
        return prev;
      }

      return {
        ...prev,
        currentUser: nextCurrentUser,
        loading,
        settings: {
          ...prev.settings,
          navTheme,
        },
      };
    });

    syncedRef.current = {
      navTheme,
      userSyncKey,
      loading,
    };
  }, [
    currentUser,
    loading,
    navTheme,
    setInitialState,
    shellConfig.featureFlags,
    userSyncKey,
  ]);

  React.useEffect(() => {
    if (loading) {
      return;
    }

    if (currentUser && hasOAuth2Session()) {
      setupAutoRefresh();
      return;
    }

    clearAutoRefresh();
  }, [currentUser, loading]);

  React.useEffect(() => {
    if (loading || !hasOAuth2Session()) {
      return;
    }

    const currentUserInfo = currentUser as
      | (Partial<CurrentUser> & { uuid?: string; id?: string })
      | null
      | undefined;
    const userUuid = String(
      currentUserInfo?.uuid || currentUserInfo?.id || '',
    ).trim();
    if (!userUuid || sysUserEnsuredForRef.current === userUuid) {
      return;
    }

    sysUserEnsuredForRef.current = userUuid;
    void ensureSysUser().catch((error) => {
      console.warn(`[sys_user] ensure failed for user=${userUuid}`, error);
      if (sysUserEnsuredForRef.current === userUuid) {
        sysUserEnsuredForRef.current = '';
      }
    });
  }, [currentUser, loading]);

  React.useEffect(() => {
    if (themeSyncedRef.current === navTheme) {
      return;
    }
    themeSyncedRef.current = navTheme;

    const useDarkMode = navTheme === 'realDark';

    setAntdConfig((prevConfig) => {
      const prevTheme = prevConfig?.theme || {};
      const normalized = (
        Array.isArray(prevTheme.algorithm)
          ? prevTheme.algorithm
          : prevTheme.algorithm
            ? [prevTheme.algorithm]
            : []
      ).filter((item) => item !== antdTheme.defaultAlgorithm);
      const hasDarkAlgorithm = normalized.includes(antdTheme.darkAlgorithm);

      if (useDarkMode === hasDarkAlgorithm) {
        return prevConfig;
      }

      const nextAlgorithms = useDarkMode
        ? [
            ...normalized.filter((item) => item !== antdTheme.darkAlgorithm),
            antdTheme.darkAlgorithm,
          ]
        : normalized.filter((item) => item !== antdTheme.darkAlgorithm);

      const nextAlgorithm =
        nextAlgorithms.length === 1
          ? nextAlgorithms[0]
          : nextAlgorithms.length > 1
            ? nextAlgorithms
            : antdTheme.defaultAlgorithm;

      return {
        ...prevConfig,
        theme: {
          ...prevTheme,
          algorithm: nextAlgorithm,
        },
      };
    });
  }, [navTheme, setAntdConfig]);

  React.useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    const syncLocale = () => {
      setRuntimeLocale(getStoredBrandLocale());
    };

    syncLocale();
    window.addEventListener('languagechange', syncLocale);
    window.addEventListener('storage', syncLocale);

    return () => {
      window.removeEventListener('languagechange', syncLocale);
      window.removeEventListener('storage', syncLocale);
    };
  }, []);

  React.useEffect(() => {
    const nextTitle = syncBrandDocument(runtimeLocale);

    if (typeof setInitialState !== 'function') {
      return;
    }

    setInitialState((prev: any) => {
      if (!prev) return prev;

      const prevSettings = prev.settings || {};
      if (prevSettings.title === nextTitle) {
        return prev;
      }

      return {
        ...prev,
        settings: {
          ...prevSettings,
          title: nextTitle,
        },
      };
    });
  }, [runtimeLocale, setInitialState]);

  return null;
};

export const rootContainer = (
  container: React.ReactNode,
  {
    setInitialState,
  }: { initialState: any; setInitialState: (updater: any) => void },
) => {
  return (
    <RootSessionContainer setInitialState={setInitialState}>
      {container}
    </RootSessionContainer>
  );
};

const RootSessionContainer: React.FC<{
  setInitialState: (updater: any) => void;
  children: React.ReactNode;
}> = ({ setInitialState, children }) => {
  const sessionConfig = React.useMemo(() => getSessionConfig(), []);

  return (
    <SessionProvider config={sessionConfig}>
      <SessionSync setInitialState={setInitialState} />
      {children}
    </SessionProvider>
  );
};

/**
 * @see https://umijs.org/docs/api/runtime-config#getinitialstate
 * */
export async function getInitialState(): Promise<{
  settings?: Partial<ProLayoutSettings & CoreLayoutSettings>;
  currentUser?: API.CurrentUser & CurrentUser;
  activeMembers?: MemberWithProfile[];
  lightHunterAccess?: LightHunterAccess | null;
  canManageConsultants?: boolean;
  consultantManagementContext?: string;
  loading?: boolean;
}> {
  const hasSession = hasCachedOAuth2Session();
  const cachedCurrentUser = hasSession ? getCachedCurrentUser() : undefined;
  const consultantManagementContext = [
    cachedCurrentUser?.tenantUuid,
    cachedCurrentUser?.organizationUuid,
    cachedCurrentUser?.memberUuid || cachedCurrentUser?.uuid,
  ].join(':');
  let activeMembers: MemberWithProfile[] = [];
  let lightHunterAccess: LightHunterAccess | null = null;
  let canManageConsultants = false;

  if (hasSession) {
    try {
      const response = await memberApi.getAllMembers({ status: 'active' });
      activeMembers = Array.isArray(response?.members) ? response.members : [];
    } catch (error) {
      console.warn('加载全局成员列表失败，已回退为空列表:', error);
    }
    try {
      lightHunterAccess = await lightHunterApi.getAccess();
    } catch (error) {
      console.warn('加载轻猎头访问权限失败，将隐藏管理菜单:', error);
    }
    canManageConsultants = await consultantPoolManagementApi
      .getConsultants('talents')
      .then(() => true)
      .catch(() => false);
  }

  return {
    loading: hasSession,
    currentUser: cachedCurrentUser,
    activeMembers,
    lightHunterAccess,
    canManageConsultants,
    consultantManagementContext,
    settings: getSettingsWithSiderWidth(getStoredBrandLocale()) as Partial<
      ProLayoutSettings & CoreLayoutSettings
    >,
  };
}

// ProLayout 支持的api https://procomponents.ant.design/components/layout
export const layout: RunTimeLayoutConfig = ({
  initialState,
  setInitialState,
}) => {
  const { tenants, currentTenantUuid } = useTenant();
  const { pathname } = useLocation();
  const screens = Grid.useBreakpoint();
  // 分栏侧栏只用一个展示分组，避免根收藏夹被分别渲染成带分隔线的分组。
  const groupCollectionRoots =
    !!screens.md &&
    initialState?.settings?.layout === 'mix' &&
    initialState?.settings?.splitMenus === true &&
    initialState?.settings?.siderMenuType === 'group';
  const getCurrentLayoutVisibility = () =>
    getLayoutVisibility(history.location.pathname);
  const appTitle = initialState?.settings?.title || getBrandAppName();
  const activeTenantUuid =
    currentTenantUuid || initialState?.currentUser?.tenantUuid;
  const organizationUuid = initialState?.currentUser?.organizationUuid;
  const managementAccessContext = [
    activeTenantUuid,
    organizationUuid,
    initialState?.currentUser?.memberUuid || initialState?.currentUser?.uuid,
  ].join(':');
  const [canManageConsultants, setCanManageConsultants] = React.useState(
    initialState?.canManageConsultants === true,
  );
  const [lightHunterChatUnreadCount, setLightHunterChatUnreadCount] =
    React.useState(0);
  const [resolvedManagementAccessContext, setResolvedManagementAccessContext] =
    React.useState(initialState?.consultantManagementContext);
  const menuCanManageConsultants =
    canManageConsultants &&
    resolvedManagementAccessContext === managementAccessContext;
  React.useEffect(() => {
    if (
      !activeTenantUuid ||
      resolvedManagementAccessContext === managementAccessContext
    ) {
      return;
    }

    let active = true;
    setCanManageConsultants(false);
    consultantPoolManagementApi
      .getConsultants('talents')
      .then(() => {
        if (active) setCanManageConsultants(true);
      })
      .catch(() => {
        if (active) setCanManageConsultants(false);
      })
      .finally(() => {
        if (active) setResolvedManagementAccessContext(managementAccessContext);
      });
    return () => {
      active = false;
    };
  }, [
    activeTenantUuid,
    managementAccessContext,
    resolvedManagementAccessContext,
  ]);
  const isCurrentTenantAdmin = tenants.some(
    (tenant) => tenant.id === activeTenantUuid && tenant.roleLabel === '管理员',
  );
  const [menuCounts, setMenuCounts] = React.useState<
    Partial<Record<(typeof COUNTABLE_MENU_PATHS)[number], number>>
  >({});
  const [talentCollections, setTalentCollections] = React.useState<
    TalentCollection[]
  >([]);
  const [clientCollections, setClientCollections] = React.useState<
    ClientCollection[]
  >([]);
  const [projectCollections, setProjectCollections] = React.useState<
    ProjectCollection[]
  >([]);
  const [collectionForm] = Form.useForm<CollectionFormValues>();
  const [collectionEditor, setCollectionEditor] = React.useState<{
    kind: CollectionKind;
    collection?: SidebarCollection;
  } | null>(null);
  const [collectionSaving, setCollectionSaving] = React.useState(false);

  const workbenchUrl = BizAppConfig.appWorkbenchUrl?.trim();
  const siderWidth =
    (
      initialState?.settings as Partial<CoreLayoutSettings> & {
        siderWidth?: number;
      }
    )?.siderWidth ?? DEFAULT_SIDER_WIDTH;

  const resizeEnabled =
    typeof window !== 'undefined' &&
    window.innerWidth > 768 &&
    initialState?.settings?.layout !== 'top' &&
    !(initialState?.settings as { collapsed?: boolean } | undefined)?.collapsed;

  const handleResizeWidth = React.useCallback(
    (nextWidth: number) => {
      setInitialState((prev) => {
        if (!prev) return prev;
        const prevSettings =
          (prev.settings as Partial<CoreLayoutSettings> & {
            siderWidth?: number;
          }) || {};
        if (prevSettings.siderWidth === nextWidth) {
          return prev;
        }
        return {
          ...prev,
          settings: {
            ...prevSettings,
            siderWidth: nextWidth,
          },
        };
      });
    },
    [setInitialState],
  );

  const loadTalentCollections = React.useCallback(async () => {
    try {
      const response = await talentCollectionApi.getCollections();
      const collections = (response as any)?.data || response || [];
      setTalentCollections(Array.isArray(collections) ? collections : []);
    } catch (error) {
      console.error('加载人才收藏夹失败:', error);
    }
  }, []);

  const loadClientCollections = React.useCallback(async () => {
    try {
      const response = await clientCollectionApi.getCollections();
      const collections = (response as any)?.data || response || [];
      setClientCollections(Array.isArray(collections) ? collections : []);
    } catch (error) {
      console.error('加载客户收藏夹失败:', error);
    }
  }, []);

  const loadProjectCollections = React.useCallback(async () => {
    try {
      const response = await projectCollectionApi.getCollections();
      const collections = (response as any)?.data || response || [];
      setProjectCollections(Array.isArray(collections) ? collections : []);
    } catch (error) {
      console.error('加载项目收藏夹失败:', error);
    }
  }, []);

  const collectionConfigs: Record<CollectionKind, CollectionConfig> = {
    talent: {
      title: '人才收藏夹',
      prefix: '/talents',
      itemPrefix: TALENT_COLLECTION_ITEM_PREFIX,
      actionPath: TALENT_COLLECTION_ACTION_PATH,
      collections: talentCollections,
      countField: 'talentCount',
      countUnit: '位人才',
      entityName: '人才',
      recordName: '人才档案',
      updatedEvent: TALENT_COLLECTIONS_UPDATED_EVENT,
      api: talentCollectionApi,
    },
    client: {
      title: '客户收藏夹',
      prefix: '/clients',
      itemPrefix: CLIENT_COLLECTION_ITEM_PREFIX,
      actionPath: CLIENT_COLLECTION_ACTION_PATH,
      collections: clientCollections,
      countField: 'clientCount',
      countUnit: '个客户',
      entityName: '客户',
      recordName: '客户资料',
      updatedEvent: 'client-collections-updated',
      api: clientCollectionApi,
    },
    project: {
      title: '项目收藏夹',
      prefix: '/projects',
      itemPrefix: PROJECT_COLLECTION_ITEM_PREFIX,
      actionPath: PROJECT_COLLECTION_ACTION_PATH,
      collections: projectCollections,
      countField: 'projectCount',
      countUnit: '个项目',
      entityName: '项目',
      recordName: '项目数据',
      updatedEvent: 'project-collections-updated',
      api: projectCollectionApi,
    },
  };
  const collectionKinds: CollectionKind[] = ['talent', 'client', 'project'];
  const activeCollectionConfig = collectionEditor
    ? collectionConfigs[collectionEditor.kind]
    : undefined;

  const openCollectionEditor = (
    kind: CollectionKind,
    collection?: SidebarCollection,
    parentUuid?: string,
  ) => {
    collectionForm.resetFields();
    collectionForm.setFieldsValue({
      name: collection?.name || '',
      description: collection?.description,
      parentUuid: collection?.parentUuid || parentUuid || null,
    });
    setCollectionEditor({ kind, collection });
  };

  const saveCollection = async () => {
    if (!collectionEditor || collectionSaving) return;
    try {
      const values = await collectionForm.validateFields();
      setCollectionSaving(true);
      const config = collectionConfigs[collectionEditor.kind];
      const data = { ...values, parentUuid: values.parentUuid || null };
      if (collectionEditor.collection) {
        await config.api.updateCollection(
          collectionEditor.collection.uuid,
          data,
        );
      } else {
        await config.api.createCollection(data);
      }
      message.success(
        collectionEditor.collection ? '收藏夹更新成功' : '收藏夹创建成功',
      );
      setCollectionEditor(null);
      collectionForm.resetFields();
      window.dispatchEvent(new CustomEvent(config.updatedEvent));
    } catch (error) {
      if (Array.isArray((error as { errorFields?: unknown[] })?.errorFields))
        return;
      showCollectionError(error, '保存收藏夹失败');
    } finally {
      setCollectionSaving(false);
    }
  };

  const deleteCollection = (
    kind: CollectionKind,
    collection: SidebarCollection,
  ) => {
    const config = collectionConfigs[kind];
    if (
      config.collections.some((item) => item.parentUuid === collection.uuid)
    ) {
      message.warning('该收藏夹下存在子收藏夹，请先删除或移出子收藏夹');
      return;
    }
    Modal.confirm({
      title: '确认删除',
      content: `删除收藏夹“${collection.name}”后，将移除其中的${config.entityName}收藏关系，${config.recordName}不受影响。`,
      okText: '删除',
      cancelText: '取消',
      okButtonProps: { danger: true },
      onOk: async () => {
        try {
          await config.api.deleteCollection(collection.uuid);
          message.success('收藏夹删除成功');
          if (
            history.location.pathname.replace(/\/+$/, '') ===
            `${config.prefix}/collection/${collection.uuid}`
          ) {
            history.push(`${config.prefix}/all`);
          }
          window.dispatchEvent(new CustomEvent(config.updatedEvent));
        } catch (error) {
          showCollectionError(error, '删除收藏夹失败');
          window.dispatchEvent(new CustomEvent(config.updatedEvent));
          throw error;
        }
      },
    });
  };

  const loadTalentMenuCounts = React.useCallback(async () => {
    try {
      const counts = await talentApi.getTalentCounts();

      setMenuCounts((prev) => ({
        ...prev,
        '/talents/all': counts.all || 0,
        '/talents/my': counts.my || 0,
        '/talents/private': counts.private || 0,
        '/talents/public': counts.public || 0,
      }));
    } catch (error) {
      console.error('加载人才菜单数量失败:', error);
    }
  }, []);

  const loadClientMenuCounts = React.useCallback(async () => {
    try {
      const counts = await clientApi.getClientCounts();

      setMenuCounts((prev) => ({
        ...prev,
        '/clients/all': counts.all || 0,
        '/clients/my': counts.my || 0,
        '/clients/private': counts.private || 0,
        '/clients/public': counts.public || 0,
      }));
    } catch (error) {
      console.error('加载客户菜单数量失败:', error);
    }
  }, []);

  const loadProjectMenuCounts = React.useCallback(async () => {
    try {
      const [allRes, myRes, recommendedRes, responsibleRes] = await Promise.all(
        [
          projectApi.getList({ page: 1, limit: 1 }),
          projectApi.getMyProjects({ page: 1, limit: 1 }),
          projectApi.getMyRecommendedProjects({ page: 1, limit: 1 }),
          projectApi.getMyResponsibleProjects({ page: 1, limit: 1 }),
        ],
      );

      setMenuCounts((prev) => ({
        ...prev,
        '/projects/all': allRes.total || 0,
        '/projects/my': myRes.total || 0,
        '/projects/recommended': recommendedRes.total || 0,
        '/projects/responsible': responsibleRes.total || 0,
      }));
    } catch (error) {
      console.error('加载项目菜单数量失败:', error);
    }
  }, []);

  React.useEffect(() => {
    loadTalentMenuCounts();
    loadClientMenuCounts();
    loadProjectMenuCounts();
    loadTalentCollections();
    loadClientCollections();
    loadProjectCollections();
  }, [
    loadClientCollections,
    loadClientMenuCounts,
    loadProjectCollections,
    loadProjectMenuCounts,
    loadTalentCollections,
    loadTalentMenuCounts,
  ]);

  React.useEffect(() => {
    dataDictionaryApi
      .preloadByCodes([...DICTIONARY_PRELOAD_CODES])
      .catch((error) => {
        console.error('预热数据字典失败:', error);
      });
  }, []);

  React.useEffect(() => {
    const onTalentsUpdated = () => {
      loadTalentMenuCounts();
    };
    const onClientsUpdated = () => {
      loadClientMenuCounts();
    };
    const onProjectsUpdated = () => {
      loadProjectMenuCounts();
    };
    const onTalentCollectionsUpdated = () => {
      loadTalentCollections();
    };
    const onClientCollectionsUpdated = () => {
      loadClientCollections();
    };
    const onProjectCollectionsUpdated = () => {
      loadProjectCollections();
    };

    window.addEventListener('talents-updated', onTalentsUpdated);
    window.addEventListener('clients-updated', onClientsUpdated);
    window.addEventListener('projects-updated', onProjectsUpdated);
    window.addEventListener(
      TALENT_COLLECTIONS_UPDATED_EVENT,
      onTalentCollectionsUpdated,
    );
    window.addEventListener(
      LEGACY_COLLECTIONS_UPDATED_EVENT,
      onTalentCollectionsUpdated,
    );
    window.addEventListener(
      'client-collections-updated',
      onClientCollectionsUpdated,
    );
    window.addEventListener(
      'project-collections-updated',
      onProjectCollectionsUpdated,
    );

    return () => {
      window.removeEventListener('talents-updated', onTalentsUpdated);
      window.removeEventListener('clients-updated', onClientsUpdated);
      window.removeEventListener('projects-updated', onProjectsUpdated);
      window.removeEventListener(
        TALENT_COLLECTIONS_UPDATED_EVENT,
        onTalentCollectionsUpdated,
      );
      window.removeEventListener(
        LEGACY_COLLECTIONS_UPDATED_EVENT,
        onTalentCollectionsUpdated,
      );
      window.removeEventListener(
        'client-collections-updated',
        onClientCollectionsUpdated,
      );
      window.removeEventListener(
        'project-collections-updated',
        onProjectCollectionsUpdated,
      );
    };
  }, [
    loadClientCollections,
    loadClientMenuCounts,
    loadProjectCollections,
    loadProjectMenuCounts,
    loadTalentCollections,
    loadTalentMenuCounts,
  ]);

  const renderMenuCountBadge = React.useCallback((value: number) => {
    const text = String(value);
    return (
      <span
        style={{
          minWidth: 32,
          height: 28,
          padding: '0 8px',
          borderRadius: 999,
          background: 'rgba(128,128,128,0.28)',
          color: 'var(--vscode-foreground)',
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: 14,
          lineHeight: 1,
          marginLeft: 8,
          fontWeight: 500,
        }}
      >
        {text}
      </span>
    );
  }, []);

  const renderMenuCount = React.useCallback(
    (path?: string, collectionCount?: number) => {
      if (collectionCount !== undefined && collectionCount !== null) {
        return renderMenuCountBadge(collectionCount);
      }
      if (!path || !COUNTABLE_MENU_PATHS.includes(path as any)) {
        return null;
      }

      const value = menuCounts[path as (typeof COUNTABLE_MENU_PATHS)[number]];
      if (value === undefined) {
        return null;
      }

      return renderMenuCountBadge(value);
    },
    [menuCounts, renderMenuCountBadge],
  );

  const renderDynamicCollectionMenuItem = (
    item: any,
    dom: React.ReactNode,
  ): React.ReactNode => {
    const path = typeof item.path === 'string' ? item.path : '';
    if (COLLECTION_GROUP_PATHS.has(path)) {
      return <span className="talent-collection-group-marker" aria-hidden />;
    }
    const kind = collectionKinds.find((entry) =>
      path.startsWith(collectionConfigs[entry].itemPrefix),
    );
    if (!kind) return dom;
    const config = collectionConfigs[kind];
    const uuid = path.slice(config.itemPrefix.length);
    if (uuid === '__tree__') {
      const toTreeData = (nodes: SidebarCollection[]): any[] =>
        nodes.map((collection) => ({
          key: collection.uuid,
          title: renderDynamicCollectionMenuItem(
            { path: `${config.itemPrefix}${collection.uuid}` },
            null,
          ),
          children: toTreeData(collection.children || []),
        }));
      const selectedUuid = config.collections.find(
        (collection) =>
          pathname.replace(/\/$/, '') ===
          `${config.prefix}/collection/${collection.uuid}`,
      )?.uuid;
      return (
        <CollectionTree
          key={kind}
          treeData={toTreeData(buildCollectionTree(config.collections))}
          selectedKeys={selectedUuid ? [selectedUuid] : []}
        />
      );
    }
    const collection = config.collections.find((entry) => entry.uuid === uuid);
    if (!collection) return dom;
    const targetPath = `${config.prefix}/collection/${collection.uuid}`;
    const count = getCollectionAggregateCount(
      config.collections,
      collection.uuid,
      config.countField,
    );
    return (
      <CollectionMenuItem
        collection={collection}
        count={count}
        countUnit={config.countUnit}
        href={targetPath}
        selected={pathname === targetPath || pathname === `${targetPath}/`}
        depth={item.collectionDepth ?? 0}
        onNavigate={() => history.push(targetPath)}
        onEdit={() => openCollectionEditor(kind, collection)}
        onCreateChild={() =>
          openCollectionEditor(kind, undefined, collection.uuid)
        }
        onDelete={() => deleteCollection(kind, collection)}
      />
    );
  };

  return {
    ...initialState?.settings,
    actionsRender: () => [],
    avatarProps: {
      render: () => {
        const { showHeader } = getCurrentLayoutVisibility();
        if (!showHeader) {
          return null;
        }
        return <AvatarDropdown />;
      },
    },
    waterMarkProps: {
      content: '',
    },
    footerRender: () => {
      const { showFooter } = getCurrentLayoutVisibility();
      if (!showFooter) {
        return false;
      }
      return <Footer />;
    },
    headerRender: (_props, dom) => {
      const { showHeader } = getCurrentLayoutVisibility();
      return showHeader ? dom : false;
    },
    menuRender: (_props, dom) => {
      const { showNav } = getCurrentLayoutVisibility();
      if (!showNav) {
        return false;
      }

      const menuRenderType = (_props as { menuRenderType?: string })
        .menuRenderType;
      const isSiderMenu = !menuRenderType || menuRenderType === 'sider';

      if (!isSiderMenu || !resizeEnabled || !dom) {
        return dom;
      }

      return (
        <>
          {dom}
          <SiderResizeHandle
            enabled
            siderWidth={siderWidth}
            onResizeWidth={handleResizeWidth}
          />
        </>
      );
    },
    headerTitleRender: (logo) => (
      <div
        onClick={() => history.push('/')}
        style={{
          alignItems: 'center',
          cursor: 'pointer',
          display: 'flex',
          gap: 8,
        }}
      >
        {logo}
        <span
          style={{
            fontSize: 18,
            fontWeight: 700,
            lineHeight: 1.2,
            color:
              initialState?.settings?.navTheme === 'realDark' ? '#000' : '#fff',
          }}
        >
          {appTitle}
        </span>
        <HeaderTitle
          logo={logo}
          appTitle={appTitle}
          workbenchUrl={workbenchUrl}
        />
      </div>
    ),
    onPageChange: () => {
      const { pathname } = history.location;
      if (pathname === '/light-hunter/members') {
        history.replace('/light-hunter/members/manage');
        return;
      }
      if (pathname.startsWith('/light-hunter/')) {
        const access = initialState?.lightHunterAccess as
          | LightHunterAccess
          | null
          | undefined;
        const isManagement = access?.isManagement === true;
        const hasTaskAccess = !access || access.permissions.viewTaskCenter;
        const memberAllowed =
          pathname === '/light-hunter/dashboard' ||
          pathname === '/light-hunter/tasks' ||
          pathname.startsWith('/light-hunter/tasks/');
        if (
          (!hasTaskAccess && pathname.startsWith('/light-hunter')) ||
          (!isManagement && !memberAllowed)
        ) {
          history.replace('/light-hunter/tasks');
          return;
        }
      }
      if (publicPaths.includes(pathname)) {
        return;
      }

      void ensureValidAccessToken();
    },
    menuHeaderRender: undefined,
    menuDataRender: (menuData) => {
      const visibleMenuData = isCurrentTenantAdmin
        ? menuData
        : menuData.filter((item) => item.path !== DATA_DICTIONARIES_PATH);

      return appendCollectionActionMenus(
        filterLightHunterMenus(
          filterConsultantManagementMenus(
            visibleMenuData,
            menuCanManageConsultants,
          ),
          initialState?.lightHunterAccess,
        ),
        talentCollections,
        clientCollections,
        projectCollections,
        groupCollectionRoots,
      );
    },
    subMenuItemRender: (item, dom) =>
      renderDynamicCollectionMenuItem(item, dom),
    menuItemRender: (item, dom) => {
      const actionKind = collectionKinds.find(
        (kind) => item.path === collectionConfigs[kind].actionPath,
      );
      if (actionKind) {
        return (
          <CollectionSectionHeader
            title={collectionConfigs[actionKind].title}
            onCreate={() => openCollectionEditor(actionKind)}
          />
        );
      }

      const isDynamicCollectionItem =
        typeof item.path === 'string' &&
        (item.path.startsWith(TALENT_COLLECTION_ITEM_PREFIX) ||
          item.path.startsWith(CLIENT_COLLECTION_ITEM_PREFIX) ||
          item.path.startsWith(PROJECT_COLLECTION_ITEM_PREFIX));

      if (isDynamicCollectionItem) {
        return renderDynamicCollectionMenuItem(item, dom);
      }

      if (item.isUrl && item.path) {
        return (
          <a href={item.path} target={item.target || '_blank'} rel="noreferrer">
            <div
              style={{
                width: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
              }}
            >
              {dom}
              {renderMenuCount(item.path as string)}
              {item.path === '/light-hunter/chat' &&
              lightHunterChatUnreadCount > 0 ? (
                <span
                  className="lh-sider-chat-badge"
                  title={`${lightHunterChatUnreadCount} 条未读消息`}
                >
                  {lightHunterChatUnreadCount > 99
                    ? '99+'
                    : lightHunterChatUnreadCount}
                </span>
              ) : null}
            </div>
          </a>
        );
      }

      if (item.path && !item.children?.length) {
        return (
          <a
            onClick={(e) => {
              e.preventDefault();
              history.push(item.path as string);
            }}
          >
            <div
              style={{
                width: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
              }}
            >
              {dom}
              {renderMenuCount(item.path as string)}
              {item.path === '/light-hunter/chat' &&
              lightHunterChatUnreadCount > 0 ? (
                <span
                  className="lh-sider-chat-badge"
                  title={`${lightHunterChatUnreadCount} 条未读消息`}
                >
                  {lightHunterChatUnreadCount > 99
                    ? '99+'
                    : lightHunterChatUnreadCount}
                </span>
              ) : null}
            </div>
          </a>
        );
      }

      return dom;
    },
    siderWidth,
    childrenRender: (children) => {
      return (
        <>
          {children}
          <Modal
            title={`${collectionEditor?.collection ? '编辑' : '新建'}${
              activeCollectionConfig?.title || '收藏夹'
            }`}
            open={!!collectionEditor}
            onCancel={() => {
              if (collectionSaving) return;
              setCollectionEditor(null);
              collectionForm.resetFields();
            }}
            onOk={saveCollection}
            confirmLoading={collectionSaving}
            closable={!collectionSaving}
            maskClosable={!collectionSaving}
            keyboard={!collectionSaving}
            destroyOnClose
            centered
            okText="保存"
            cancelText="取消"
            cancelButtonProps={{ disabled: collectionSaving }}
          >
            <Form
              form={collectionForm}
              layout="vertical"
              disabled={collectionSaving}
            >
              <Form.Item
                label="收藏夹名称"
                name="name"
                rules={[
                  {
                    required: true,
                    whitespace: true,
                    message: '请输入收藏夹名称',
                  },
                  { max: 100, message: '收藏夹名称不能超过100个字符' },
                ]}
              >
                <Input placeholder="请输入收藏夹名称" />
              </Form.Item>
              <Form.Item label="上级收藏夹" name="parentUuid">
                <TreeSelect
                  allowClear
                  showSearch
                  treeNodeFilterProp="title"
                  treeDefaultExpandAll
                  treeLine
                  placeholder="根收藏夹"
                  treeData={buildCollectionTreeSelectData(
                    activeCollectionConfig?.collections || [],
                    collectionEditor?.collection?.uuid,
                  )}
                />
              </Form.Item>
              <Form.Item
                label="收藏夹描述"
                name="description"
                rules={[{ max: 500, message: '描述不能超过500个字符' }]}
              >
                <Input.TextArea
                  rows={4}
                  placeholder="请输入收藏夹描述"
                  showCount
                  maxLength={500}
                />
              </Form.Item>
            </Form>
          </Modal>
          <React.Suspense fallback={null}>
            <LightHunterChatUnreadObserver
              onUnreadCountChange={setLightHunterChatUnreadCount}
            />
          </React.Suspense>
          {isDev && (
            <SettingDrawer
              disableUrlParams
              enableDarkTheme
              settings={initialState?.settings}
              onSettingChange={(settings) => {
                setInitialState((preInitialState) => ({
                  ...preInitialState,
                  settings,
                }));
              }}
            />
          )}
        </>
      );
    },
  };
};
